import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { PergolaUpgradeCta } from "@/components/PergolaUpgradeCta";
import { DropCurtainForm } from "@/components/DropCurtainForm";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";
import { watchVideos } from "@/data/watchVideos";
import hero from "@/assets/original/stock-curtains.jpg";
import heroPicture from "@/assets/original/stock-curtains.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import redVinylFabric from "@/assets/curtains-red-vinyl-fabric.jpg";
import installedPatio from "@/assets/curtains-installed-patio.jpg";
import dropCurtainInstall from "@/assets/drop-curtain-install-phifertex-mesh.jpg";
import clearVinylPavilion from "@/assets/clear-vinyl-drop-curtains-pavilion-enclosure.jpg";
import clearVinylGolfPavilion from "@/assets/clear-vinyl-pavilion-enclosure-golf-course.jpg";
import manualRollUpCurtainsSanAntonio from "@/assets/curtains/manual-roll-up-curtains-san-antonio.jpg.asset.json";

export const Route = createFileRoute("/curtains")({
  head: (ctx) => ({
      links: [canonicalLink(pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title: "Outdoor Curtains & Motorized Drop Shades — San Antonio & Austin | Texas Made Shade",
        description: "Custom outdoor curtains, motorized drop shades & side-track screens for San Antonio & Austin patios. Sunbrella, Phifertex mesh, clear vinyl. Solair® dealer.",
        image: hero,
        socialTitle: "Motorized Drop Shades & Outdoor Curtains — San Antonio & Austin",
        socialDescription: "Motorized drop shades, side-track screens & custom outdoor curtains — built in Texas, installed across San Antonio, Austin & the Hill Country.",
        keywords: "outdoor curtains San Antonio, outdoor curtains Austin, manual roll up curtains San Antonio, motorized patio shades Austin, motorized shades San Antonio, motorized shades Austin TX, motorized outdoor shades with side tracks, automatic patio shade, smart shades Austin TX, drop curtains Texas, roll up curtains, Phifertex mesh curtains, clear vinyl drop curtains, patio enclosure curtains, awning company San Antonio, Austin awning company, Texas Made Shade",
      }),
    scripts: [
      ...serviceHeadScripts({
        path: "/curtains",
        serviceName: "Outdoor Curtains & Drop Panels",
        breadcrumbName: "Curtains",
        description:
          "Roll-down vinyl and mesh outdoor curtains, drop panels and patio enclosures — fabricated and installed across Texas.",
      }),
    ],
  }),

  component: () => (
    <ServicePage
      currentPath="/curtains"
      serviceName="Curtains"
      eyebrow="★ CURTAINS & PANELS ★"
      title="Outdoor Curtains & Motorized Drop Shades in Texas"
      intro="Manual roll-up curtains, custom fabric and vinyl panels, plus motorized patio screens for San Antonio, Austin and Texas outdoor spaces. Compare sewn curtains with side-track exterior shades for your opening."
      heroImage={heroPicture}
      hideLetsTalkForm
      preContent={(
        <section className="bg-white py-12">
          <div className="mx-auto max-w-5xl px-4 lg:px-8 text-lg text-navy-deep/85">
            <nav aria-label="Curtain and motorized screen options" className="mb-8 flex flex-wrap gap-3">
              <Button asChild><Link to="/exterior-vertical-shades">Solair® Motorized Patio Screens</Link></Button>
              <Button asChild variant="outline"><Link to="/motorized-drop-screens/in/$city" params={{ city: "san-antonio" }}>San Antonio Drop Screens</Link></Button>
              <Button asChild variant="outline"><a href="#lets-talk">Request a Curtain Quote</a></Button>
            </nav>
            <figure className="mb-4 ml-0 w-full overflow-hidden border-2 border-navy-deep/15 shadow-card sm:float-left sm:mr-6 sm:mb-3 sm:w-[48%] md:w-[44%] lg:w-[42%]">
              <img
                src={clearVinylGolfPavilion}
                alt="Clear vinyl drop-curtain enclosure on a Texas country-club pavilion — wraparound see-through PVC panels with white binding turn the open-air pavilion into a wind-and-rain-tight room while keeping the view of the green lawn and trees outside"
                loading="lazy"
                fetchPriority="low"
                decoding="async"
                width={1356}
                height={1808}
                className="block h-auto w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                Clear vinyl pavilion enclosure — country-club install
              </figcaption>
            </figure>
            <p>
              <strong>Clear vinyl drop curtains</strong> turn an open-air pavilion, party barn or back porch into a usable, wind-and-rain-tight room without giving up the view. The pavilion shown here is wrapped in heavy-gauge clear PVC, bound in white webbing and tensioned tight against the posts so the wind can't beat it loose — but the light, the trees and the green outside still come through like a wall of glass.
            </p>
            <p className="mt-4">
              We pattern every panel on-site to your exact opening, then sew it on industrial machines in our Seguin shop with reinforced binding, brass grommets and marine-grade zippers. Drop them when a cold front blows in off the Gulf, roll them up when the weather breaks, and run your patio, restaurant or event space year-round.
            </p>
            <p className="mt-4">
              Pair the clear panels with <strong>Phifertex® mesh drop curtains</strong> on the sun-facing sides for shade and breeze, or with <strong>Sunbrella® fabric drapes</strong> for privacy and softness — every system is built to your opening, your weather and how you actually use the space.
            </p>
            <div className="clear-both" />

            <div className="mt-12" id="drop-curtains-video">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-navy-deep">
                Watch: Drop Curtains In Action
              </h2>
              <Link to="/videos/drop-curtains" className="mt-4 block max-w-sm" aria-label="Watch Drop Curtains in Action">
                <img src={watchVideos.curtains.poster} alt="Installer demonstrating a mesh drop curtain" width={900} height={1200} loading="lazy" className="h-auto w-full" />
              </Link>
              <Button asChild className="mt-4"><Link to="/videos/drop-curtains"><Play aria-hidden="true" />Watch Drop Curtains in Action</Link></Button>
            </div>

          </div>
        </section>
      )}
      features={[
        "Manual roll-up curtains with brass cleats and pulley systems",
        "Screen mesh panels for bug protection and airflow",
        "Outdoor fabric curtains in Sunbrella & Tempotest",
        "Solid vinyl panels for wind and rain protection",
        "Custom-sewn drapes for indoor commercial spaces",
        "Track, grommet, snap, and zipper systems available",
      ]}
      body={[
        { heading: "Solair® Exterior Vertical Shades & Motorized Drop Screens", text: "Push-button motorized drop screens with side tracks for San Antonio and Austin patios. Sunbrella® and Phifertex® mesh fabrics, Somfy® motors, optional wind sensors and smart-home control. See the full Solair® Exterior Vertical Shades page for sizes, fabrics and the comparison vs. Phantom-style screens." },
        { heading: "Manual Roll Curtains", text: "Hand-cranked or pull-strap roll curtains for porches, patios, and outdoor kitchens. Roll up out of the way when not needed, drop down for sun, wind or privacy." },
        { heading: "Screen Mesh Panels", text: "Heavy-duty insect screen mesh panels keep mosquitos and wasps out while letting the Texas breeze through. Perfect for porches and outdoor dining areas." },
        { heading: "Outdoor Fabric Curtains", text: "Premium Sunbrella and Tempotest curtains hung on tracks, grommets, or rod systems. Fade-resistant, washable, and built to take Texas weather." },
        { heading: "Solid Vinyl Panels", text: "Clear or opaque vinyl panels with marine zippers and snap systems — close in a porch for the cold months, open it back up for spring." },
      ]}
      faqs={[
        {
                "q": "What's the difference between drop curtains and roll-up curtains?",
                "a": "Drop curtains slide along a track and stack to one side — best for restaurants and patios that open and close daily. Roll-up curtains roll vertically into a header and lock at any height — best for trailers, equipment enclosures and patio walls used seasonally."
        },
        {
                "q": "Can outdoor curtains keep out wind and rain?",
                "a": "Yes — solid vinyl and Sunbrella® curtains block wind and rain almost completely. Phifertex® mesh curtains block sun and 70–90% of wind while letting air pass for restaurants and patios. We help you pick the right material based on how you'll use the space."
        },
        {
                "q": "Do you install outdoor curtains for restaurants in San Antonio?",
                "a": "Yes — we install commercial outdoor curtains and patio enclosures across San Antonio, New Braunfels, Austin and the Hill Country. We handle restaurants, breweries, country clubs and event venues with engineered tracks and commercial-grade hardware."
        },
        {
                "q": "How are outdoor curtains attached to my patio?",
                "a": "Curtains hang from welded aluminum tracks, header brackets or a tensioned cable system bolted into your existing patio cover, pergola or building. We pattern on-site so the curtains seal flush against posts and corners."
        }
]}
      applications={["Residential Patios", "Restaurant Patios", "Outdoor Kitchens", "Pool Cabanas", "Commercial Storefronts", "RV Awnings"]}
      cityCallouts={[
        {
          to: "/seguin",
          city: "Seguin",
          anchorText: "Custom curtains sewn in our Seguin shop",
          blurb: "Roll curtains, screen mesh and vinyl drop panels patterned and sewn in our Seguin, TX shop on industrial machines.",
        },
        {
          to: "/san-antonio",
          city: "San Antonio",
          anchorText: "Drop curtains for San Antonio patios & restaurants",
          blurb: "Custom roll curtains and screen mesh panels installed across San Antonio patios, restaurant courtyards and outdoor bars.",
        },
      ]}
      gallerySection={{
        eyebrow: "Shop & Install",
        title: "Custom Curtain Build & Finished Patio Install",
        intro: (
          <>
            <div className="border-2 border-navy-deep bg-navy-deep text-white shadow-card">
              <div className="grid gap-0 md:grid-cols-[1.4fr_1fr]">
                <div className="p-6 md:p-8">
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                    ★ Free Estimates · Same-Day Response
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-bold leading-tight md:text-3xl">
                    Get a free estimate on your custom curtains.
                  </h3>
                  <p className="mt-3 text-base text-white/85">
                    Roll-up curtains, screen panels, vinyl drops, or Sunbrella drapes — send a few photos and rough opening measurements and we'll come back same business day with a free, no-pressure estimate.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href={`/#lets-talk?type=${encodeURIComponent("Curtains / Roll Curtains / Panels")}&notes=${encodeURIComponent("Curtain inquiry — please describe location (patio, porch, restaurant, etc.), opening size, and curtain type (roll-up, screen mesh, vinyl, fabric).")}`}
                      className="inline-flex items-center gap-2 bg-red px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-red/90"
                    >
                      Get a Free Estimate
                    </a>
                    <a
                      href="tel:+18303909588"
                      className="inline-flex items-center gap-2 border-2 border-white px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
                    >
                      Call +1 (830) 390-9588
                    </a>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 border-2 border-white/40 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white/90 transition hover:border-white hover:text-white"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
                <div className="flex flex-col justify-center gap-2 border-t-2 border-white/15 bg-cream p-6 text-navy-deep md:border-l-2 md:border-t-0 md:p-8">
                  <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
                    Why Homeowners Choose Us
                  </div>
                  <ul className="mt-2 space-y-2 font-display text-sm font-semibold uppercase tracking-wider text-navy-deep">
                    <li>✓ Built in our Seguin, TX shop</li>
                    <li>✓ Marine-grade fabric &amp; hardware</li>
                    <li>✓ Free measure &amp; estimate</li>
                    <li>✓ Trusted across South Texas</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-8 text-lg text-navy-deep/80">
              <figure className="mb-4 ml-0 w-full overflow-hidden border-2 border-navy-deep/15 shadow-card sm:float-right sm:ml-6 sm:mb-3 sm:w-[46%] md:w-[42%] lg:w-[38%]">
                <img
                  src={clearVinylPavilion}
                  alt="Clear vinyl drop-curtain enclosure on a Texas pavilion — wraparound see-through panels with white binding and zipper closures, protecting wood picnic tables from wind and rain while keeping the view of the green lawn outside"
                  loading="lazy"
                  decoding="async"
                  width={1356}
                  height={1808}
                  className="block h-auto w-full object-cover"
                />
                <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                  Clear vinyl drop curtains — wraparound pavilion enclosure
                </figcaption>
              </figure>
              <p>
                Watch a custom curtain come together — from heavy-duty red vinyl rolled out and patterned on our Seguin cutting table to a finished, ready-to-install panel. Marine-grade vinyl blocks wind-driven rain, knocks down summer sun, and stands up to years of Texas weather without cracking or fading. Each panel is measured to your opening, edge-hemmed for strength, and finished with the hardware that fits how you'll use it — grommets, snaps, zippers, or a roll-up track.
              </p>
              <p className="mt-4">
                For open-air pavilions, party barns and outdoor event spaces, we build wraparound <strong>clear vinyl drop curtains</strong> like the enclosure shown here — see-through panels with reinforced binding, brass grommets and marine-grade zippers that turn an open structure into a wind-and-rain-tight room in minutes. Drop them when a cold front blows in, roll them up when the weather breaks, and keep the view of your property either way.
              </p>
              <div className="clear-both" />
            </div>
          </>
        ),
        columns: 2,
        images: [
          {
            src: redVinylFabric,
            alt: "Red vinyl curtain fabric being patterned and cut on the cutting table in the Texas Made Shade Seguin shop",
            label: "Fabric · Heavy-Duty Red Vinyl",
            caption: "The fabric stage — heavy-duty red vinyl rolled out, measured, and patterned to your opening on our Seguin cutting table.",
          },
          {
            src: installedPatio,
            alt: "Finished cream Sunbrella roll-up patio curtains installed on a Texas residential porch with stone columns and wood ceiling",
            label: "Finished Install · Roll-Up Patio Curtains",
            caption: "The finished install — cream Sunbrella roll-up curtains hung on the patio. Drop them for shade & privacy, roll them up for the breeze.",
          },
          {
            src: dropCurtainInstall,
            alt: "Texas Made Shade installer in a marlin-print shirt and ball cap kneeling on a Central Texas back patio while fastening a tan Phifertex mesh drop curtain to the brick column with a brass turn-button and pull cord",
            label: "On-Site Install · Phifertex Mesh Drop Curtain",
            caption: "Out at the install — our crew tensioning a tan Phifertex® outdoor mesh drop curtain against a brick patio column. Brass turn-buttons and a pull cord let the homeowner drop the panel for afternoon sun and roll it back up to keep the breeze and the view.",
          },
          {
            src: manualRollUpCurtainsSanAntonio.url,
            alt: "Manual roll-up outdoor curtains on a San Antonio area patio with wood header and stone columns — beige fabric panels rolled down to block sun and neighbors while keeping the outdoor view",
            label: "Manual Roll-Up Curtains · San Antonio",
            caption: "Manual roll-up curtains installed on a San Antonio area patio — roll the panels down for shade, privacy and wind protection, then roll them back up when you want the full breeze and view.",
          },
        ],
      }}
      postCta={<PergolaUpgradeCta />}
    >
      <DropCurtainForm />
    </ServicePage>
  ),
});
